/* Money to the penny, matching the backend: these figures are what the IO
   prints and totals, so a budget keeps the pence it was entered with. */
const toPence = (value: number): number => Math.round(value * 100) / 100;

/** Resolve value-add absolute from % or absolute input. */
function resolveValueAddAbsolute(
  inputGross: number,
  bookedNet: number,
  opts: {
    valueAddType?: 'VALUE_POT' | 'ADDED_VALUE' | string | null;
    valueAddInputMode?: 'PERCENT' | 'ABSOLUTE' | string | null;
    valueAddPercent?: number | null;
    valueAddAbsolute?: number | null;
  }
): number {
  const gross = Number.isFinite(inputGross) && inputGross > 0 ? inputGross : 0;
  const net = Number.isFinite(bookedNet) && bookedNet > 0 ? bookedNet : 0;
  // Added value % is of gross; value pot % is of net.
  const percentBase = opts.valueAddType === 'ADDED_VALUE' ? gross : net;

  if (opts.valueAddInputMode === 'ABSOLUTE') {
    const abs = Number(opts.valueAddAbsolute ?? 0);
    const amount = Number.isFinite(abs) && abs > 0 ? toPence(abs) : 0;
    if (opts.valueAddType === 'ADDED_VALUE') return amount;
    return Math.min(amount, toPence(net));
  }
  const pct = Number(opts.valueAddPercent ?? 0);
  const valueAddPercent = Number.isFinite(pct) && pct > 0 ? Math.min(100, Math.max(0, pct)) : 0;
  return toPence((percentBase * valueAddPercent) / 100);
}

type ValueOpts = {
  applyAgencyDiscount?: boolean | null;
  planAgencyDiscount?: number | null;
  valueAddType?: 'VALUE_POT' | 'ADDED_VALUE' | string | null;
  valueAddInputMode?: 'PERCENT' | 'ABSOLUTE' | string | null;
  valueAddPercent?: number | null;
  valueAddAbsolute?: number | null;
  storedGrossBudget?: number | null;
  storedNetBudget?: number | null;
};

function bookedNetFromGross(
  gross: number,
  opts: Pick<ValueOpts, 'applyAgencyDiscount' | 'planAgencyDiscount'>
): number | undefined {
  const planDisc = opts.planAgencyDiscount;
  if (planDisc == null || !Number.isFinite(Number(planDisc))) return undefined;
  const disc = opts.applyAgencyDiscount === false ? 0 : Number(planDisc);
  return Math.max(0, gross - (gross * disc) / 100);
}

/**
 * Display gross:
 *   VALUE_POT → entered budget
 *   ADDED_VALUE → entered budget + value
 */
export function resolveLineGrossBudget(
  budget: number | null | undefined,
  opts: ValueOpts = {}
): number | undefined {
  const gross = Number(budget);
  if (!Number.isFinite(gross)) return undefined;

  const bookedNet = bookedNetFromGross(gross, opts);
  if (bookedNet != null) {
    const valueAdd = resolveValueAddAbsolute(gross, bookedNet, opts);
    if (opts.valueAddType === 'ADDED_VALUE') {
      return toPence(gross + valueAdd);
    }
    return toPence(gross);
  }

  if (opts.storedGrossBudget != null && Number.isFinite(Number(opts.storedGrossBudget))) {
    return Number(opts.storedGrossBudget);
  }
  return toPence(gross);
}

/**
 * Display net:
 *   VALUE_POT → bookedNet − value
 *   ADDED_VALUE → bookedNet (revenue unchanged)
 */
export function resolveLineNetBudget(
  budget: number | null | undefined,
  opts: ValueOpts = {}
): number | undefined {
  const gross = Number(budget);
  if (!Number.isFinite(gross)) return undefined;

  const bookedNet = bookedNetFromGross(gross, opts);
  if (bookedNet != null) {
    const valueAdd = resolveValueAddAbsolute(gross, bookedNet, opts);
    if (opts.valueAddType === 'ADDED_VALUE') {
      return toPence(bookedNet);
    }
    return Math.max(0, toPence(bookedNet - valueAdd));
  }

  if (opts.storedNetBudget != null && Number.isFinite(Number(opts.storedNetBudget))) {
    return Number(opts.storedNetBudget);
  }

  return toPence(gross);
}
